import React, {FC} from "react";
import {Collapse, Descriptions, Space} from "antd";
import moment from "moment";
import i18n from "../../services/i18n";
import {ScheduledPaymentType} from "../../generated/graphql";
import {RenderAmount} from "../TableColumnRenderers/TableColumnRenderers";
import {calcPaymentTotals, CancelSplitPost, splitCancelledPosts} from "./paymentTotals";
import {CANCEL_REST_LABELS, CancelRestHandling} from "../../../shared/cancelSettlementPlan";
import {roundToCents} from "../../../shared/money";

type CancelInfo = {
    canceled?: string | null;
    cancelRestHandling?: string | null;
    cancelRestAmount?: number | null;
    cancelSettledAmount?: number | null;
    cancelRestNote?: string | null;
    cancelWaivedFees?: Array<{title: string; amount: number}> | null;
};

type Post = CancelSplitPost & {id: string; title?: string | null; type: ScheduledPaymentType};

type Props = {
    booking?: unknown;
    scheduledPayments: readonly Post[];
    isCanceled: boolean;
};

/**
 * Ergebnis des Stornos (ADR 0022, Testlauf 30.10.2026 Befunde 17/20/21): Block „Storno vom …“ und eingeklappte
 * „Erlassene Posten“. Eigene Komponente, weil Schulen mit Mollie statt der Zahlungskarte die Mollie-Ansicht sehen
 * und beide den Block brauchen (Review #1797).
 */
const CancelSummary: FC<Props> = ({booking, scheduledPayments, isCanceled}) => {
    if (!isCanceled || !booking) {
        return null;
    }
    const cancelInfo = booking as CancelInfo;
    const {counted, waived, waivedAmount} = splitCancelledPosts(scheduledPayments, isCanceled);
    const {openAmount} = calcPaymentTotals(counted);
    // Beim Storno erlassene Gebühren sind gelöscht bzw. gekürzt und stehen nicht mehr in den Posten; die Buchung hält sie fest
    const waivedFees = cancelInfo.cancelWaivedFees ?? [];
    const waivedTotal = roundToCents(waivedAmount + waivedFees.reduce((sum, fee) => sum + fee.amount, 0));
    const tr = i18n.containers.invoice.InvoiceDetails;

    return (
        <>
        {(
            <Descriptions size="small" column={1} bordered style={{marginTop: 16}} title={tr.cancelBlock.title({date: cancelInfo.canceled ? moment(cancelInfo.canceled).format("DD.MM.YYYY") : "–"})} data-testid="cancel-block">
                {(cancelInfo.cancelSettledAmount ?? 0) > 0 && (
                    <Descriptions.Item label={tr.cancelBlock.settled()}>
                        <RenderAmount amount={cancelInfo.cancelSettledAmount ?? 0} style={{fontSize: "unset", fontWeight: "unset"}} />
                    </Descriptions.Item>
                )}
                {(cancelInfo.cancelRestAmount ?? 0) > 0 && (
                    <Descriptions.Item label={tr.cancelBlock.rest()}>
                        <Space>
                            <RenderAmount amount={cancelInfo.cancelRestAmount ?? 0} style={{fontSize: "unset", fontWeight: "unset"}} />
                            {cancelInfo.cancelRestHandling && <span>{CANCEL_REST_LABELS[cancelInfo.cancelRestHandling as CancelRestHandling] ?? cancelInfo.cancelRestHandling}</span>}
                        </Space>
                    </Descriptions.Item>
                )}
                {cancelInfo.cancelRestNote && <Descriptions.Item label={tr.cancelBlock.note()}>{cancelInfo.cancelRestNote}</Descriptions.Item>}
                {(openAmount ?? 0) > 0 && (
                    <Descriptions.Item label={tr.cancelBlock.openFees()}>
                        <RenderAmount amount={openAmount ?? 0} style={{fontSize: "unset", fontWeight: "unset"}} />
                    </Descriptions.Item>
                )}
            </Descriptions>
        )}
        {isCanceled && (waived.length > 0 || waivedFees.length > 0) && (
            <Collapse ghost style={{marginTop: 8}} data-testid="waived-posts">
                <Collapse.Panel
                    key="waived"
                    header={
                        <Space>
                            {tr.cancelBlock.waived()}
                            <RenderAmount amount={waivedTotal} style={{fontSize: "unset", fontWeight: "unset"}} />
                        </Space>
                    }
                >
                    {waived.map((p) => (
                        <div key={p.id} style={{display: "flex", justifyContent: "space-between"}}>
                            <span>{p.title || (p.type === ScheduledPaymentType.Standard ? "Kursgebühr" : "Zusatzposten")}</span>
                            <RenderAmount amount={Number(p.openAmount ?? p.paymentAmount)} style={{fontSize: "unset", fontWeight: "unset"}} />
                        </div>
                    ))}
                    {waivedFees.map((fee, index) => (
                        <div key={`fee-${index}`} style={{display: "flex", justifyContent: "space-between"}}>
                            <span>{fee.title}</span>
                            <RenderAmount amount={fee.amount} style={{fontSize: "unset", fontWeight: "unset"}} />
                        </div>
                    ))}
                </Collapse.Panel>
            </Collapse>
        )}
        </>
    );
};

export default CancelSummary;
